import { Anchor, Checkbox, Group, Loader, Stack, Text } from '@mantine/core';
import { schemaResolver, type UseFormReturnType, useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { i18nKeys, useTranslation } from 'locales';
import type { MouseEvent } from 'react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { isEmpty, sumBy } from 'remeda';
import { type CommunicationLocale, CommunicationLocaleSchema, languageName } from 'types/locale';
import { z } from 'zod/v4';

import {
  type AvailableCommunicationLocale,
  useLoadCommunicationLocales,
  useUpdateCommunicationLocales,
} from '../api';

const CommunicationLocalesFormSchema = z.object({
  locales: z.array(CommunicationLocaleSchema).min(1),
});

type CommunicationLocalesFormValues = z.infer<typeof CommunicationLocalesFormSchema>;

type CommunicationLocalesFieldsProps = {
  form: UseFormReturnType<CommunicationLocalesFormValues>;
};

export const CommunicationLocalesFields = ({ form }: CommunicationLocalesFieldsProps) => {
  const { t } = useTranslation();

  const { communicationLocales, isCommunicationLocalesLoading } = useLoadCommunicationLocales();

  const hintFor = (language: AvailableCommunicationLocale) => {
    const { COMMUNICATION_LANGUAGES } = i18nKeys.FORM.PREFERENCES;

    if (language.isDefault) return t(COMMUNICATION_LANGUAGES.DEFAULT_HINT);

    if (!language.enabled) return null;

    if (!language.canDisable) {
      const { debtors, contactPeople } = language.usage;

      const clients = debtors > 0 && (
        <Anchor
          component={Link}
          to={`/clients?locale=${language.value}`}
          size="xs"
          underline="always"
          onClick={stopPropagation}
        >
          {t(COMMUNICATION_LANGUAGES.CLIENTS_COUNT, { count: debtors })}
        </Anchor>
      );

      const contacts =
        contactPeople > 0 &&
        t(COMMUNICATION_LANGUAGES.CONTACT_PERSONS_COUNT, { count: contactPeople });

      return (
        <>
          {t(COMMUNICATION_LANGUAGES.USED_BY)} {clients}
          {clients && contacts ? ` ${t(i18nKeys.AND)} ` : null}
          {contacts}
        </>
      );
    }

    return t(COMMUNICATION_LANGUAGES.ENABLED_HINT, {
      templateBodies: language.usage.templateBodies,
    });
  };

  return (
    <Stack my="md" gap="md">
      <Text size="sm" c="dimmed">
        {t(i18nKeys.FORM.PREFERENCES.COMMUNICATION_LANGUAGES.DESCRIPTION)}
      </Text>

      {isCommunicationLocalesLoading ? (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      ) : (
        <Checkbox.Group {...form.getInputProps('locales')}>
          <Stack gap="sm">
            {communicationLocales.available.map(language => (
              <Checkbox
                key={language.value}
                value={language.value}
                label={languageName(language.value)}
                description={hintFor(language)}
                disabled={language.enabled && !language.canDisable}
              />
            ))}
          </Stack>
        </Checkbox.Group>
      )}
    </Stack>
  );
};

type UseCommunicationLocalesFormOptions = {
  onSaved?: () => void;
};

export const useCommunicationLocalesForm = ({
  onSaved,
}: UseCommunicationLocalesFormOptions = {}) => {
  const { t } = useTranslation();

  const { communicationLocales } = useLoadCommunicationLocales();
  const { updateCommunicationLocales, isUpdateCommunicationLocalesLoading } =
    useUpdateCommunicationLocales();

  const form = useForm<CommunicationLocalesFormValues>({
    initialValues: { locales: [] },
    validate: schemaResolver(CommunicationLocalesFormSchema, { sync: true }),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync the loaded set into the form only when the fetched data changes, not on every render (the form object identity is unstable)
  useEffect(() => {
    form.setInitialValues({ locales: communicationLocales.enabled });
    form.setValues({ locales: communicationLocales.enabled });
  }, [communicationLocales]);

  const save = (locales: CommunicationLocale[]) =>
    updateCommunicationLocales(
      { locales },
      {
        onSuccess: () => {
          form.resetDirty();
          onSaved?.();
        },
      },
    );

  const handleSave = () => {
    const { locales } = form.getValues();
    const removed = communicationLocales.available.filter(
      language => language.enabled && !locales.includes(language.value),
    );

    if (isEmpty(removed)) {
      save(locales);
      return;
    }

    modals.openConfirmModal({
      title: t(i18nKeys.FORM.PREFERENCES.COMMUNICATION_LANGUAGES.CONFIRM_TITLE),
      centered: true,
      children: (
        <Text size="sm">
          {t(i18nKeys.FORM.PREFERENCES.COMMUNICATION_LANGUAGES.CONFIRM_BODY, {
            count: sumBy(removed, language => language.usage.templateBodies),
            languages: removed.map(language => languageName(language.value)).join(', '),
          })}
        </Text>
      ),
      labels: { confirm: t(i18nKeys.CONFIRM), cancel: t(i18nKeys.CANCEL) },
      confirmProps: { color: 'red' },
      onConfirm: () => save(locales),
    });
  };

  return { form, handleSave, isSaving: isUpdateCommunicationLocalesLoading };
};

function stopPropagation(event: MouseEvent) {
  event.stopPropagation();
}
